import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const categories = [
  { slug: "all", name: "All" },
  { slug: "arabica", name: "Arabica" },
  { slug: "robusta", name: "Robusta" },
  { slug: "single-origin", name: "Single Origin" },
  { slug: "blends", name: "Blends" },
  { slug: "decaf", name: "Decaf" },
  { slug: "equipment", name: "Equipment" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const category = params.get("category") || "all";
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    setLoading(true);
    client
      .get(`/products${category !== "all" ? `?category=${category}` : ""}`)
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category]);

  const sorted = useMemo(() => {
    const list = [...products];
    if (sort === "price-asc") list.sort((a, b) => (a.variants?.[0]?.price || a.price) - (b.variants?.[0]?.price || b.price));
    if (sort === "price-desc") list.sort((a, b) => (b.variants?.[0]?.price || b.price) - (a.variants?.[0]?.price || a.price));
    return list;
  }, [products, sort]);

  return (
    <div data-testid="shop-page" className="pt-32 pb-24 max-w-[1600px] mx-auto px-6 md:px-12">
      <div className="mb-12">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#C85A32] mb-4">The Shop</div>
        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light leading-none">
          Every cup, a<br />conversation with the estate.
        </h1>
      </div>

      <div className="flex flex-col md:flex-row justify-between md:items-center gap-6 border-y border-[#EBE3D5] py-6 mb-16">
        <div className="flex flex-wrap gap-x-8 gap-y-3">
          {categories.map((c) => (
            <button
              key={c.slug}
              data-testid={`filter-${c.slug}`}
              onClick={() => {
                if (c.slug === "all") setParams({});
                else setParams({ category: c.slug });
              }}
              className={`text-xs uppercase tracking-[0.2em] link-underline ${
                category === c.slug ? "text-[#C85A32]" : "text-[#2C1E16]"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
        <select
          data-testid="sort-select"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="bg-transparent border border-[#EBE3D5] px-4 py-2 text-xs uppercase tracking-[0.2em] focus:outline-none focus:border-[#1C3326]"
        >
          <option value="newest">Newest</option>
          <option value="price-asc">Price: Low to High</option>
          <option value="price-desc">Price: High to Low</option>
        </select>
      </div>

      {loading ? (
        <div className="py-32 text-center font-serif-display text-3xl text-[#7B6F66]">
          Brewing…
        </div>
      ) : sorted.length === 0 ? (
        <div className="py-32 text-center font-serif-display text-3xl text-[#7B6F66]">
          No products in this category yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16" data-testid="product-grid">
          {sorted.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
